/**
 * Admin → edit a single field rep (profile + territories).
 *
 * Pulls the rep's current state via /api/admin/field/reps/{id} on
 * open, then PATCHes back the changed fields on save.
 */

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { fetchAdminRep, updateAdminRep } from '../../api/field';
import { HomeStatePicker, TerritoryStatePicker } from '../field/StatePickers';

const INK = '#0f0a07';
const GOLD = '#bb8c3f';
const GOLD_LIGHT = '#e9c46a';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

interface AdminFieldRepDialogProps {
  open: boolean;
  userId: string;
  onClose: () => void;
}

export function AdminFieldRepDialog({ open, userId, onClose }: AdminFieldRepDialogProps) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['admin', 'field', 'rep', userId],
    queryFn: () => fetchAdminRep(userId),
    enabled: open,
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            bgcolor: '#fdf9f0',
            borderRadius: 2,
            border: '1px solid rgba(187, 140, 63, 0.22)',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: FONT_INTER,
          fontWeight: 700,
          fontSize: 17,
          color: INK,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        Edit Representative
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: 'rgba(187, 140, 63, 0.22)' }}>
        {query.isLoading && (
          <Stack spacing={2}>
            <Skeleton variant="rectangular" height={40} />
            <Skeleton variant="rectangular" height={40} />
            <Skeleton variant="rectangular" height={40} />
          </Stack>
        )}
        {query.error && (
          <Alert severity="error" sx={{ fontFamily: FONT_INTER }}>
            {(query.error as Error).message}
          </Alert>
        )}
        {query.data && (
          <RepEditForm
            rep={query.data}
            onSaved={() => {
              queryClient.invalidateQueries({ queryKey: ['admin', 'field'] });
              onClose();
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function RepEditForm({
  rep,
  onSaved,
}: {
  rep: {
    user_id: string;
    email: string;
    first_name: string;
    last_name: string;
    home_address: string | null;
    home_city: string | null;
    home_state: string | null;
    home_zip: string | null;
    phone: string | null;
    territory_states: string[];
    is_active: boolean;
  };
  onSaved: () => void;
}) {
  const [homeAddress, setHomeAddress] = useState(rep.home_address ?? '');
  const [homeCity, setHomeCity] = useState(rep.home_city ?? '');
  const [homeState, setHomeState] = useState(rep.home_state ?? '');
  const [homeZip, setHomeZip] = useState(rep.home_zip ?? '');
  const [phone, setPhone] = useState(rep.phone ?? '');
  const [territories, setTerritories] = useState<string[]>(rep.territory_states);

  useEffect(() => {
    setHomeAddress(rep.home_address ?? '');
    setHomeCity(rep.home_city ?? '');
    setHomeState(rep.home_state ?? '');
    setHomeZip(rep.home_zip ?? '');
    setPhone(rep.phone ?? '');
    setTerritories(rep.territory_states);
  }, [rep]);

  // Auto-default: when admin sets the rep's home state, include it in
  // the territory list (admin can still remove via the chip's ×).
  useEffect(() => {
    if (!homeState) return;
    if (territories.includes(homeState)) return;
    setTerritories([...territories, homeState].sort((a, b) => a.localeCompare(b)));
  }, [homeState, territories]);

  const mutation = useMutation({
    mutationFn: () =>
      updateAdminRep(rep.user_id, {
        home_address: homeAddress.trim() || null,
        home_city: homeCity.trim() || null,
        home_state: homeState.trim().toUpperCase() || null,
        home_zip: homeZip.trim() || null,
        phone: phone.trim() || null,
        territory_states: territories,
      }),
    onSuccess: onSaved,
  });

  return (
    <Stack spacing={2}>
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontWeight: 700,
          fontSize: 14,
          color: INK,
        }}
      >
        {rep.first_name} {rep.last_name}
      </Typography>
      <Typography
        sx={{
          mt: -1.5,
          fontFamily: FONT_INTER,
          fontSize: 12.5,
          color: 'rgba(15, 10, 7, 0.55)',
        }}
      >
        {rep.email}
      </Typography>

      <Box>
        <TerritoryStatePicker value={territories} onChange={setTerritories} />
        <Typography
          sx={{
            mt: 0.75,
            fontFamily: FONT_INTER,
            fontSize: 11.5,
            color: 'rgba(15, 10, 7, 0.55)',
          }}
        >
          A rep can cover multiple states — pick all that they’re responsible for. Their home state
          is added automatically when set.
        </Typography>
      </Box>

      <Box sx={{ borderTop: '1px dashed rgba(187, 140, 63, 0.22)', pt: 1.5 }}>
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: 'rgba(15, 10, 7, 0.55)',
            mb: 1,
          }}
        >
          Home address
        </Typography>
        <TextField
          label="Street"
          value={homeAddress}
          onChange={(e) => setHomeAddress(e.target.value)}
          fullWidth
          size="small"
          inputProps={{ maxLength: 200 }}
          sx={{ mb: 1.5 }}
        />
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <TextField
            label="City"
            value={homeCity}
            onChange={(e) => setHomeCity(e.target.value)}
            fullWidth
            size="small"
            inputProps={{ maxLength: 100 }}
          />
          <HomeStatePicker value={homeState} onChange={setHomeState} width={180} />
          <TextField
            label="ZIP"
            value={homeZip}
            onChange={(e) => setHomeZip(e.target.value)}
            sx={{ width: 140 }}
            size="small"
            inputProps={{ maxLength: 10 }}
          />
        </Box>
        <TextField
          label="Phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          fullWidth
          size="small"
          inputProps={{ maxLength: 30 }}
          sx={{ mt: 1.5 }}
        />
      </Box>

      {mutation.error && (
        <Alert severity="error" sx={{ fontFamily: FONT_INTER }}>
          {(mutation.error as Error).message}
        </Alert>
      )}

      <DialogActions sx={{ p: 0 }}>
        <Button
          variant="contained"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate()}
          sx={{
            background: `linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, #8e6a2a 100%)`,
            color: INK,
            fontFamily: FONT_INTER,
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            fontSize: 12,
            px: 2.25,
            boxShadow: 'none',
            '&:hover': {
              background: `linear-gradient(135deg, #efc960 0%, #c79747 55%, #936e2b 100%)`,
              boxShadow: '0 4px 14px rgba(187, 140, 63, 0.22)',
            },
          }}
        >
          {mutation.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogActions>
    </Stack>
  );
}
